import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Tag,
  message,
} from "antd";
import "./AdminTools.css";
import { useAuth } from "../AuthProvider";
import APIService from "../../Service/APIService";
import Table from "../../common/table";
import TableOptionsCell from "../../common/table/table-options-cell";
import TableLink from "../../common/table/table-link";

const CATEGORY_NAME_MAX = 255;

const ProductCategories = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiServiceRef = React.useRef(new APIService());
  const [categoryForm] = Form.useForm();
  const [moveForm] = Form.useForm();

  const [categories, setCategories] = React.useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = React.useState(false);
  const [showDeleted, setShowDeleted] = React.useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState(null);
  const [isSavingCategory, setIsSavingCategory] = React.useState(false);
  const [isMutating, setIsMutating] = React.useState(false);
  const [productGroupsCategoryID, setProductGroupsCategoryID] =
    React.useState(null);
  const [movingProductGroup, setMovingProductGroup] = React.useState(null);
  const [isMovingProductGroup, setIsMovingProductGroup] = React.useState(false);

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback;

  const fetchCategories = React.useCallback(async () => {
    setIsCategoriesLoading(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        "getproductgroupcategories"
      );
      setCategories(response?.data?.data || []);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load product categories"));
    } finally {
      setIsCategoriesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const userData = user;
    if (!userData) return;

    if (userData.Type !== "Admin" || !userData.IsSuperAdmin) {
      navigate("/");
      return;
    }

    fetchCategories();
  }, [fetchCategories, navigate, user]);

  const visibleCategories = React.useMemo(
    () =>
      showDeleted
        ? categories
        : categories.filter((category) => category.IsActive),
    [categories, showDeleted]
  );

  const activeCategories = React.useMemo(
    () => categories.filter((category) => category.IsActive),
    [categories]
  );

  // Re-derived from the refreshed list (rather than held in state) so the open
  // modal reflects moves and renames without being reopened.
  const productGroupsCategory = React.useMemo(
    () =>
      productGroupsCategoryID
        ? categories.find(
            (category) => Number(category.ID) === Number(productGroupsCategoryID)
          ) || null
        : null,
    [categories, productGroupsCategoryID]
  );

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    categoryForm.resetFields();
  };

  const openCreateCategoryModal = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({ Category: "" });
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue({ Category: category?.Category });
    setIsCategoryModalOpen(true);
  };

  const saveCategory = async (name) => {
    setIsSavingCategory(true);
    try {
      if (editingCategory) {
        await apiServiceRef.current.updateDataToBackend(
          { ID: editingCategory.ID, Category: name },
          "updateproductgroupcategory"
        );
        message.success("Product category updated successfully");
      } else {
        await apiServiceRef.current.addDataToBackend(
          { Category: name },
          "addproductgroupcategory"
        );
        message.success("Product category created successfully");
      }
      closeCategoryModal();
      await fetchCategories();
    } catch (error) {
      // The modal stays open on a duplicate name so the name can be corrected.
      message.error(getErrorMessage(error, "Unable to save product category"));
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleSaveCategory = async () => {
    let values;
    try {
      values = await categoryForm.validateFields();
    } catch (error) {
      return;
    }

    const name = (values.Category || "").trim();
    const assignedCount = Number(editingCategory?.ProductGroupCount || 0);

    if (editingCategory && name === editingCategory.Category) {
      closeCategoryModal();
      return;
    }

    if (editingCategory && assignedCount > 0) {
      Modal.confirm({
        title: "Rename this product category?",
        content: `This category is assigned to ${assignedCount} product group${
          assignedCount === 1 ? "" : "s"
        }. The new name will be shown on all of them.`,
        okText: "Rename",
        cancelText: "Cancel",
        onOk: () => saveCategory(name),
      });
      return;
    }

    await saveCategory(name);
  };

  const handleDeleteCategory = (category) => {
    const assignedCount = Number(category?.ProductGroupCount || 0);
    if (assignedCount > 0) return;

    Modal.confirm({
      title: "Delete this product category?",
      content: `"${category.Category}" will no longer be available when creating or editing product groups. You can restore it later.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        setIsMutating(true);
        try {
          await apiServiceRef.current.deleteDataFromBackend(
            `deleteproductgroupcategory/${encodeURIComponent(category.ID)}`
          );
          message.success("Product category deleted successfully");
        } catch (error) {
          message.error(
            getErrorMessage(error, "Unable to delete product category")
          );
        } finally {
          setIsMutating(false);
          await fetchCategories();
        }
      },
    });
  };

  const handleRestoreCategory = async (category) => {
    setIsMutating(true);
    try {
      await apiServiceRef.current.addDataToBackend(
        { ID: category.ID },
        "restoreproductgroupcategory"
      );
      message.success("Product category restored successfully");
    } catch (error) {
      message.error(getErrorMessage(error, "Unable to restore product category"));
    } finally {
      setIsMutating(false);
      await fetchCategories();
    }
  };

  const openProductGroupsModal = (category) => {
    setProductGroupsCategoryID(category?.ID ?? null);
  };

  const closeProductGroupsModal = () => {
    setProductGroupsCategoryID(null);
  };

  const moveTargetOptions = React.useMemo(
    () =>
      activeCategories
        .filter(
          (category) =>
            Number(category.ID) !== Number(productGroupsCategory?.ID)
        )
        .map((category) => ({ value: category.ID, label: category.Category })),
    [activeCategories, productGroupsCategory]
  );

  const openMoveModal = (productGroup) => {
    setMovingProductGroup(productGroup);
    moveForm.resetFields();
    moveForm.setFieldsValue({ ProductGroupCategoryID: null });
  };

  const closeMoveModal = () => {
    setMovingProductGroup(null);
    moveForm.resetFields();
  };

  const handleMoveProductGroup = async () => {
    let values;
    try {
      values = await moveForm.validateFields();
    } catch (error) {
      return;
    }

    setIsMovingProductGroup(true);
    try {
      await apiServiceRef.current.addDataToBackend(
        {
          ProductGroupID: movingProductGroup.ID,
          ProductGroupCategoryID: values.ProductGroupCategoryID,
        },
        "moveproductgrouptocategory"
      );
      const targetName =
        moveTargetOptions.find(
          (option) => option.value === values.ProductGroupCategoryID
        )?.label || "the selected category";
      message.success(`Product group moved to ${targetName}`);
      closeMoveModal();
      await fetchCategories();
    } catch (error) {
      message.error(getErrorMessage(error, "Unable to move product group"));
    } finally {
      setIsMovingProductGroup(false);
    }
  };

  const categoryColumns = [
    {
      headerName: "Product Category",
      field: "Category",
      filter: true,
      minWidth: 320,
      cellRenderer: (params) => (
        <span>
          {params.data?.Category || "-"}
          {params.data?.IsActive ? null : (
            <Tag style={{ marginLeft: 8 }}>Deleted</Tag>
          )}
        </span>
      ),
    },
    {
      headerName: "Product Groups",
      field: "ProductGroupCount",
      filter: true,
      minWidth: 160,
    },
    {
      headerName: "Status",
      field: "IsActive",
      filter: true,
      minWidth: 140,
      cellRenderer: (params) =>
        params.data?.IsActive ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag>Deleted</Tag>
        ),
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      filter: false,
      minWidth: 320,
      cellRenderer: (params) => {
        const category = params.data;
        if (!category) return null;

        if (!category.IsActive) {
          return (
            <TableOptionsCell
              optionOne="Restore"
              optionOneType="success"
              optionOneDisabled={isMutating}
              optionOneClickFn={() => handleRestoreCategory(category)}
              rowData={category}
            />
          );
        }

        const assignedCount = Number(category.ProductGroupCount || 0);
        return (
          <TableOptionsCell
            optionOne="Delete"
            optionOneType="failure"
            optionOneDisabled={assignedCount > 0 || isMutating}
            optionOneTitle={
              assignedCount > 0
                ? "Move or remove the assigned product groups before deleting"
                : undefined
            }
            optionOneClickFn={() => handleDeleteCategory(category)}
            optionTwo="Edit"
            optionTwoType="warning"
            optionTwoClickFn={() => openEditCategoryModal(category)}
            optionThree={`View Product Groups (${assignedCount})`}
            optionThreeClickFn={() => openProductGroupsModal(category)}
            rowData={category}
          />
        );
      },
    },
  ];

  const productGroupColumns = [
    {
      headerName: "Product Group Name",
      field: "ProductGroupName",
      filter: true,
      minWidth: 240,
      cellRenderer: (params) =>
        params.data?.ManufacturerID && params.data?.ID ? (
          <TableLink
            value={params.data.ProductGroupName || "-"}
            path={`/Manufacturer/${params.data.ManufacturerID}/ProductGroup/${params.data.ID}`}
          />
        ) : (
          params.data?.ProductGroupName || "-"
        ),
    },
    {
      headerName: "Manufacturer",
      field: "ManufacturerName",
      filter: true,
      minWidth: 200,
      cellRenderer: (params) => params.data?.ManufacturerName || "-",
    },
    {
      headerName: "Status",
      field: "IsActive",
      filter: true,
      minWidth: 140,
      cellRenderer: (params) =>
        params.data?.IsActive ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag>Inactive</Tag>
        ),
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      filter: false,
      minWidth: 140,
      cellRenderer: (params) => (
        <TableOptionsCell
          optionOne="Move"
          optionOneType="warning"
          optionOneDisabled={moveTargetOptions.length === 0}
          optionOneTitle={
            moveTargetOptions.length === 0
              ? "Create another active product category first"
              : undefined
          }
          optionOneClickFn={() => openMoveModal(params.data)}
          rowData={params.data}
        />
      ),
    },
  ];

  return (
    <div className="admin-tools-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="text text-h1">Manage Product Categories</div>
          <div className="text text-smallText admin-tools-subtitle">
            Manage the product categories available when creating and editing
            product groups.
          </div>
        </div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/AdminTools")}
        >
          Back to Admin Tools
        </Button>
      </div>

      <div className="admin-tools-row">
        <div className="admin-tools-banner-alerts">
          <div className="admin-tools-banner-alerts-header">
            <div>
              <div className="text text-h2">All Product Categories</div>
              <div className="text text-smallText admin-tools-subtitle">
                Renaming a category updates it everywhere it is used. A category
                cannot be deleted while product groups are assigned to it.
              </div>
            </div>
            <div className="product-categories-header-actions">
              <div className="product-categories-show-deleted">
                <Switch
                  checked={showDeleted}
                  onChange={setShowDeleted}
                  id="show-deleted-product-categories"
                />
                <span className="text text-smallText">Show deleted</span>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateCategoryModal}
              >
                New Product Category
              </Button>
            </div>
          </div>

          <Table
            rowData={visibleCategories}
            columnDefs={categoryColumns}
            loading={isCategoriesLoading}
          />
        </div>
      </div>

      <Modal
        title={
          editingCategory ? "Edit Product Category" : "New Product Category"
        }
        open={isCategoryModalOpen}
        onCancel={closeCategoryModal}
        onOk={handleSaveCategory}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isSavingCategory}
        destroyOnClose
        width={480}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            label="Product Category Name"
            name="Category"
            rules={[
              {
                required: true,
                message: "Product Category Name is required",
              },
              {
                max: CATEGORY_NAME_MAX,
                message: `Product Category Name cannot exceed ${CATEGORY_NAME_MAX} characters`,
              },
            ]}
          >
            <Input maxLength={CATEGORY_NAME_MAX} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Product Groups — ${productGroupsCategory?.Category || ""}`}
        open={!!productGroupsCategory}
        onCancel={closeProductGroupsModal}
        footer={[
          <Button key="close" onClick={closeProductGroupsModal}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {productGroupsCategory?.ProductGroups?.length ? (
          <Table
            rowData={productGroupsCategory.ProductGroups}
            columnDefs={productGroupColumns}
            pagination={false}
          />
        ) : (
          <div className="text text-smallText">
            No product groups are assigned to this category.
          </div>
        )}
      </Modal>

      <Modal
        title="Move Product Group"
        open={!!movingProductGroup}
        onCancel={closeMoveModal}
        onOk={handleMoveProductGroup}
        okText="Move"
        cancelText="Cancel"
        confirmLoading={isMovingProductGroup}
        destroyOnClose
        width={480}
      >
        <div className="text text-bodyText">
          Move <strong>{movingProductGroup?.ProductGroupName || ""}</strong> to a
          different product category.
        </div>
        <div className="spacer-16" />
        <Form form={moveForm} layout="vertical">
          <Form.Item
            label="Product Category"
            name="ProductGroupCategoryID"
            rules={[
              { required: true, message: "Product Category is required" },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={moveTargetOptions}
              placeholder="Select Product Category"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductCategories;
